import { FileText, Loader2, FolderOpen } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { type TeachingMaterial, useTeachingMaterials, formatBytes } from "@/lib/teaching-materials"
import { useSharedDocument } from "./SharedDocument"

interface DocumentPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Tutor-only: pick a PDF from the teaching-materials library to share onto the
// classroom board. Sharing propagates to the student via the shared Yjs state.
export function DocumentPicker({ open, onOpenChange }: DocumentPickerProps) {
  const { t } = useTranslation()
  const { data: materials = [], isLoading } = useTeachingMaterials()
  const { open: shareDocument } = useSharedDocument()

  const pick = (material: TeachingMaterial) => {
    shareDocument(material)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("liveSession.documentPicker.title")}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          {t("liveSession.documentPicker.description")}
        </p>

        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("liveSession.documentPicker.loading")}
          </div>
        ) : materials.length === 0 ? (
          <div className="py-8 text-center space-y-2">
            <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mx-auto">
              <FolderOpen className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              {t("liveSession.documentPicker.empty")}
            </p>
          </div>
        ) : (
          <div className="space-y-2 mt-1 max-h-80 overflow-y-auto">
            {materials.map((material) => (
              <button
                key={material.id}
                onClick={() => pick(material)}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-secondary/30 hover:bg-secondary/60 transition-colors text-left"
              >
                <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{material.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("liveSession.documentPicker.materialMeta", { size: formatBytes(material.byteSize) })}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
